import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, ChevronDown, Inbox } from 'lucide-react';
import { add, format, max, min, parse, subtract, zero } from '@fint/money';
import { DEPOSIT_KINDS, DEPOSIT_KIND_LABELS, type DepositKind } from '@fint/validation';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useIsPhone } from '@/hooks/useIsPhone';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { useDebounced } from '@/hooks/useDebounced';
import { formatDate, formatDateLong, formatDateTime } from '@/lib/dates';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/app/DashboardLayout';
import {
  Badge, Button, Card, CardFacts, CardLine, CardRow, Cards, ColourChip, ColourDot, EmptyState, ErrorState, Input,
  Money, PastDescriptions, SelectField, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, SkeletonTable, SuggestChips, SuggestedLine,
  TBody, TD, TH, THead, TR, Table, Textarea, colourSolid,
} from '@/features/common/ui';
import { categoryLabel } from '@/features/quick-add/quick-add';
import { inboxKey, useInbox, type InboxBatch, type InboxBook, type InboxItem } from './useInboxCount';
import { groupByBook, movable, moveNotice, moveTargets, type BookRef, type Ref } from './inbox-books';
import {
  KIND_ICON,
  KIND_LABEL,
  beforeAfterLine,
  hasOwnAmount,
  importedFieldsOf,
  isImportedInvoice,
  isSettlement,
  originallyLine,
  changeKindLabel,
  rowActionFor,
  settlementFigures,
  type SettlementMoneyKit,
} from './inbox-drafts';
import { ChangeEditSheet, CustomerEditSheet, DiscardDraft, ItemEditSheet } from './DraftSheets';
import { Note } from './fields';
import {
  EMPTY_SUGGESTIONS,
  applyToBatchLabel,
  batchRowsForPayee,
  isUndecided,
  savedNotice,
  suggestedLine,
  unambiguousCategory,
  type SuggestType,
  type Suggestions,
} from './inbox-suggest';

/**
 * The Inbox: what a model proposed, waiting for a person - across every book.
 *
 * ── WHY THIS SCREEN EXISTS ──
 * A model may propose any record; a person confirms it in Fint; nothing a
 * model does is in any figure until then (docs/design/mcp-fluency.md §3).
 * The expense list already shows one draft with one Confirm button. This is
 * the same act made survivable at 142 rows: a spreadsheet's worth, grouped as
 * the batch it arrived in, every row ticked, untick the two that are wrong,
 * one press. The confirm is the existing per-record confirm, one row at a
 * time on the server, so a row that fails leaves the others confirmed and
 * says why beside itself.
 *
 * ── WHY IT SPANS THE BOOKS ──
 * Claude drafts into whichever book it was told, and the person reading the
 * Inbox is one person with two books. An Inbox that showed only the book on
 * the sidebar would be quiet about the other, and the way to see it would be
 * to switch and look - which is how a proposal sits unconfirmed for a month.
 * So: every book, with a colour-coded button per book to narrow the list,
 * and every batch marked with the book it is in. Confirm, discard and edit
 * still go to the row's OWN book, under its own id: the server decides per
 * book, and the page groups the requests the same way.
 *
 * ── WHY A ROW CAN MOVE ──
 * "Drafted in the company's book; it was the flat's electricity." The row is
 * a draft in both directions - in no figure before, in no figure after - and
 * the server re-drafts it in the target through the same path a fresh
 * proposal takes, resolving the category and the account by name there. What
 * it could not match it says in the outcome, beside the row or in the line
 * above the list.
 *
 * ── WHY EVERYTHING IS TICKED BY DEFAULT ──
 * The person's job is to find what is WRONG, not to re-approve what is right.
 * A hundred rows to tick is a hundred chances to skip one; a hundred rows to
 * scan for the odd one is what a spreadsheet already trained them to do. So
 * the state kept is the exceptions - what was unticked - and a row arriving
 * from the next poll is ticked without anybody wiring it.
 *
 * ── WHY AN INVOICE IS NOT CONFIRMED HERE ──
 * Sending is the act that makes an invoice a figure, and that act stays on
 * the invoice page. The row links there; Confirm refuses it with the same
 * sentence the server uses, so the two never disagree.
 *
 * ── WHY THE CATALOGUE AND A CHANGE ARRIVE HERE TOO ──
 * "Give access to create catalogue items and customers - but not complete
 * ones. Only drafts in inbox and I'll later confirm it. Attaching customers
 * and items to appropriate incomes which are already in." So a draft item
 * and a draft customer are rows here (in no picker, on no invoice until
 * confirmed), and a CHANGE to a deposit already in the books - who paid,
 * for what, filed as which kind of income - is a row that shows what it
 * would do as before → after, and touches the deposit only on Confirm. The
 * three sit in the same batch card as the money rows they came with, so
 * "PKPP Tech, Consulting, and attach both to the 12 Aug deposit" reads as
 * the one proposal it was. None of them moves between books (see
 * `canMove`), so the Move menu counts only the rows that can.
 *
 * ── WHY AN IMPORTED INVOICE IS THE EXCEPTION TO THE EXCEPTION ──
 * "If I upload income invoices from a different account, it should create
 * everything - items, customers - and create new invoices exactly like the
 * old references and attach the original to it as source." An invoice
 * recreated from Zoho's PDF was SENT from Zoho already; there is no act of
 * sending left to do, only the fact to record. So its row carries the
 * number it had there ("originally INV-2024-017 in Zoho"), the original as
 * a link, and a button that says what Confirm does to it: "Record as
 * issued". A home-grown draft keeps the link to its page and the server's
 * refusal - the two are told apart by `imported`, never by guesswork.
 *
 * ── WHY A SETTLEMENT SHOWS TWO FIGURES ──
 * A settlement is the one change that moves a figure: the ₹50,000 that
 * arrived in August and was counted as income becomes the payment of the
 * invoice that was in the old system. The row shows the deposit as Banking
 * shows it, the invoice it would settle, and the balance before and after,
 * because the person is deciding whether that deposit was that invoice -
 * and says in a line that confirming records a payment, since nothing else
 * in the Inbox does.
 */

const keyOf = (ref: Ref) => `${ref.type}:${ref.id}`;

/** The `@fint/money` functions a settlement row needs - handed in, as overview-months does; see `settlementFigures`. */
const MONEY: SettlementMoneyKit = { parse, add, subtract, format, zero, max, min };

interface Outcome {
  confirmed: Ref[];
  failed: Array<Ref & { reason: string }>;
}

interface MoveOutcome {
  moved: Array<Ref & { newId: string; note: string | null }>;
  failed: Array<Ref & { reason: string }>;
}

export function InboxPage() {
  const org = useActiveOrg();
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  const query = useInbox();
  const [unticked, setUnticked] = useState<Set<string>>(() => new Set());
  const [failures, setFailures] = useState<Map<string, string>>(() => new Map());
  const [editing, setEditing] = useState<InboxItem | null>(null);
  /** Which book's rows are shown. Null is every book. */
  const [bookFilter, setBookFilter] = useState<string | null>(null);
  // How the last action went, in one sentence above the list. The per-row
  // reasons are beside the rows; this is the count.
  const [notice, setNotice] = useState<string | null>(null);

  const books = useMemo(() => query.data?.books ?? [], [query.data]);
  const batches = useMemo(() => {
    const all = query.data?.batches ?? [];
    return bookFilter ? all.filter((batch) => batch.orgId === bookFilter) : all;
  }, [query.data, bookFilter]);
  const allRefs = useMemo(
    () => batches.flatMap((b) => b.items.map((i): BookRef => ({ type: i.type, id: i.id, orgId: b.orgId }))),
    [batches],
  );
  const selected = useMemo(() => allRefs.filter((ref) => !unticked.has(keyOf(ref))), [allRefs, unticked]);
  /**
   * Whether every ticked row is an imported invoice, so the one button can
   * say what it is about to do to them: record them as issued, sending
   * nothing. A mixed selection keeps "Confirm", which is the honest word for
   * a customer, an item, a deposit and a settlement at once.
   */
  const onlyImported = useMemo(() => {
    const ticked = new Set(selected.map(keyOf));
    const rows = batches.flatMap((b) => b.items).filter((i) => ticked.has(keyOf(i)));
    return rows.length > 0 && rows.every(isImportedInvoice);
  }, [batches, selected]);

  const isTicked = (item: Ref) => !unticked.has(keyOf(item));
  const toggle = (item: Ref) =>
    setUnticked((current) => {
      const next = new Set(current);
      const key = keyOf(item);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const setBatch = (batch: InboxBatch, ticked: boolean) =>
    setUnticked((current) => {
      const next = new Set(current);
      for (const item of batch.items) {
        if (ticked) next.delete(keyOf(item));
        else next.add(keyOf(item));
      }
      return next;
    });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: inboxKey(org?.id) });
    // Confirming touches every figure a draft was kept out of.
    void queryClient.invalidateQueries({ queryKey: ['expenses'] });
    void queryClient.invalidateQueries({ queryKey: ['banking'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    // A confirmed item or customer is in every picker now; a confirmed
    // change is on the deposit, which Payments lists by who paid.
    void queryClient.invalidateQueries({ queryKey: ['items'] });
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
    void queryClient.invalidateQueries({ queryKey: ['payments-direct'] });
    // A settlement records a payment and moves an invoice's balance; an
    // imported invoice becomes issued on its own page.
    void queryClient.invalidateQueries({ queryKey: ['payments'] });
    void queryClient.invalidateQueries({ queryKey: ['invoice'] });
  };

  const act = useMutation({
    /**
     * One request per book, in turn, and the outcomes added up. The server
     * decides each row under the book it is in; the page never asks one book
     * about another's rows.
     */
    mutationFn: async ({ path, items }: { path: 'confirm' | 'discard'; items: BookRef[] }) => {
      const outcome: Outcome = { confirmed: [], failed: [] };
      for (const [orgId, refs] of groupByBook(items)) {
        const part = await api.post<Outcome>(`/inbox/${path}`, { items: refs }, { orgId });
        outcome.confirmed.push(...part.confirmed);
        outcome.failed.push(...part.failed);
      }
      return outcome;
    },
    onSuccess: (outcome, { path }) => {
      // The rows that failed stay - they are still drafts - with the server's
      // sentence beside them. Everything else is gone from the next fetch.
      setFailures(new Map(outcome.failed.map((f) => [keyOf(f), f.reason])));
      const verb = path === 'confirm' ? 'Confirmed' : 'Discarded';
      setNotice(
        outcome.failed.length
          ? `${verb} ${outcome.confirmed.length} of ${outcome.confirmed.length + outcome.failed.length}. ` +
            `${outcome.failed.length} could not be - the reason is beside each one.`
          : `${verb} ${outcome.confirmed.length}.`,
      );
      invalidate();
    },
  });

  /** One row, through the same discard the bulk bar uses - so a refusal reads the same. */
  const discardOne = async (item: InboxItem, orgId: string) => {
    const outcome = await act.mutateAsync({ path: 'discard', items: [{ type: item.type, id: item.id, orgId }] });
    if (outcome.failed.length) throw new Error(outcome.failed[0].reason);
  };

  const move = useMutation({
    mutationFn: async ({ items, to }: { items: BookRef[]; to: InboxBook }) => {
      const outcome: MoveOutcome = { moved: [], failed: [] };
      for (const [orgId, refs] of groupByBook(items)) {
        // A row already in the target has nowhere to go.
        if (orgId === to.orgId) continue;
        const part = await api.post<MoveOutcome>('/inbox/move', { items: refs, toOrgId: to.orgId }, { orgId });
        outcome.moved.push(...part.moved);
        outcome.failed.push(...part.failed);
      }
      return outcome;
    },
    onSuccess: (outcome, { to }) => {
      setFailures(new Map(outcome.failed.map((f) => [keyOf(f), f.reason])));
      setNotice(moveNotice(outcome, to.name));
      invalidate();
    },
  });

  const busy = act.isPending || move.isPending;
  const count = selected.length;
  const error = act.error ?? move.error;
  /** Whether the in-flight confirm is THIS row's own "Record as issued", so only its button spins. */
  const confirmingOne = (item: Ref) =>
    act.isPending &&
    act.variables?.path === 'confirm' &&
    act.variables.items.length === 1 &&
    keyOf(act.variables.items[0]) === keyOf(item);
  const total = query.data?.total ?? 0;
  const caveats = query.data?.caveats ?? [];

  return (
    <div>
      <PageHeader
        title="Inbox"
        subtitle="What your AI agent proposed. Nothing here is in any figure until you confirm it."
        actions={
          total > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {books.length > 1 ? (
                <MoveMenu
                  books={books}
                  selected={selected}
                  disabled={count === 0 || busy}
                  loading={move.isPending}
                  onMove={(to) => move.mutate({ items: movable(selected), to })}
                />
              ) : null}
              <Button
                variant="outline"
                disabled={count === 0 || busy}
                loading={busy && act.variables?.path === 'discard'}
                onClick={() => act.mutate({ path: 'discard', items: selected })}
              >
                Discard {count} selected
              </Button>
              <Button
                disabled={count === 0 || busy}
                loading={busy && act.variables?.path === 'confirm'}
                onClick={() => act.mutate({ path: 'confirm', items: selected })}
              >
                {onlyImported ? `Record ${count} as issued` : `Confirm ${count} selected`}
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="space-y-4 px-4 pb-6 sm:px-5">
        {books.length > 1 ? <BookButtons books={books} total={total} value={bookFilter} onChange={setBookFilter} /> : null}

        {notice ? (
          <p role="status" className="rounded-md bg-muted px-3 py-2 text-sm">
            {notice}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error instanceof Error ? error.message : 'Something went wrong'}
          </p>
        ) : null}

        {query.isError ? (
          <Card>
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          </Card>
        ) : query.isLoading ? (
          <Card>
            <SkeletonTable rows={4} cols={5} />
          </Card>
        ) : total === 0 ? (
          <Card>
            <EmptyState
              icon={Inbox}
              title="Nothing waiting."
              description="When your AI agent proposes records, they arrive here."
            />
          </Card>
        ) : batches.length === 0 ? (
          <Card>
            <EmptyState
              icon={Inbox}
              title={`Nothing waiting in ${books.find((b) => b.orgId === bookFilter)?.name ?? 'this book'}.`}
              description="The other books have rows - pick one above, or All books."
            />
          </Card>
        ) : (
          batches.map((batch) => {
            const tickedHere = batch.items.filter(isTicked).length;
            return (
              <Card key={`${batch.orgId}:${batch.id ?? 'single'}`} data-testid="inbox-batch" data-org-id={batch.orgId}>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 sm:px-5">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-sm font-semibold">{batch.title}</h2>
                      {/* Which book, on every batch and not only when filtered:
                          a person on "All books" is exactly the person who
                          needs to see it. */}
                      <span data-testid="inbox-book-chip">
                        <ColourChip colour={batch.colour} name={batch.orgName} />
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {batch.items.length === 1 ? '1 row' : `${batch.items.length} rows`}
                      {batch.source === 'mcp' ? ' from your AI agent' : ''}
                      {batch.createdAt ? ` · ${formatDateTime(batch.createdAt)}` : ''}
                    </p>
                    {batch.note ? <Note text={batch.note} data-testid="inbox-batch-note" /> : null}
                  </div>
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-input"
                      checked={tickedHere === batch.items.length}
                      ref={(el) => {
                        if (el) el.indeterminate = tickedHere > 0 && tickedHere < batch.items.length;
                      }}
                      onChange={(e) => setBatch(batch, e.target.checked)}
                      aria-label={`Select all in ${batch.title}`}
                    />
                    <span>{tickedHere} of {batch.items.length} selected</span>
                  </label>
                </div>

                {isPhone ? (
                  <Cards>
                    {batch.items.map((item) => (
                      <CardRow key={keyOf(item)} data-testid="inbox-row" data-kind={item.type} data-imported={isImportedInvoice(item) || undefined}>
                        <CardLine
                          title={<LabelWithEmoji item={item} truncate />}
                          meta={
                            item.type === 'change'
                              ? undefined
                              : isImportedInvoice(item)
                                ? item.label
                                : (item.description ?? item.detail ?? undefined)
                          }
                          value={<RowAmount item={item} />}
                          sub={kindLabel(item)}
                        />
                        {item.type === 'change' ? (
                          <div className="mt-1">
                            <ChangeLines item={item} />
                          </div>
                        ) : null}
                        {isImportedInvoice(item) ? <SourceFileLink item={item} orgId={batch.orgId} /> : null}
                        {item.proposalNote ? <Note text={item.proposalNote} clamp data-testid="inbox-row-note" /> : null}
                        <CardFacts>
                          <label className="-ml-1 flex min-h-11 items-center gap-1.5 px-1">
                            <input
                              type="checkbox"
                              className="size-4 rounded border-input"
                              checked={isTicked(item)}
                              onChange={() => toggle(item)}
                              aria-label={`Select ${item.label}`}
                            />
                            <span>Select</span>
                          </label>
                          <span>{rowDate(item)}</span>
                          <RowAction
                            item={item}
                            onEdit={() => setEditing(item)}
                            onDiscard={() => discardOne(item, batch.orgId)}
                            onConfirm={() => act.mutate({ path: 'confirm', items: [{ type: item.type, id: item.id, orgId: batch.orgId }] })}
                            confirming={confirmingOne(item)}
                            disabled={busy}
                          />
                        </CardFacts>
                        <Failure reason={failures.get(keyOf(item))} />
                      </CardRow>
                    ))}
                  </Cards>
                ) : (
                  <Table>
                    <THead>
                      <TR>
                        <TH className="w-8">
                          <span className="sr-only">Selected</span>
                        </TH>
                        <TH>Date</TH>
                        <TH>Filed as</TH>
                        <TH>Description</TH>
                        <TH>Type</TH>
                        <TH className="text-right">Amount</TH>
                        <TH className="w-24">
                          <span className="sr-only">Actions</span>
                        </TH>
                      </TR>
                    </THead>
                    <TBody>
                      {batch.items.map((item) => {
                        const reason = failures.get(keyOf(item));
                        // Prose wraps; a money row's label stays one line.
                        const prose = item.type === 'change' || isImportedInvoice(item);
                        return (
                          <TR key={keyOf(item)} data-testid="inbox-row" data-kind={item.type} data-imported={isImportedInvoice(item) || undefined}>
                            <TD>
                              {/* A 44px label around the box: the cell is the
                                  target, and a finger beside the box still ticks it. */}
                              <label className="-m-2.5 flex size-11 cursor-pointer items-center justify-center">
                                <input
                                  type="checkbox"
                                  className="size-4 rounded border-input"
                                  checked={isTicked(item)}
                                  onChange={() => toggle(item)}
                                  aria-label={`Select ${item.label}`}
                                />
                              </label>
                            </TD>
                            <TD className="whitespace-nowrap">{rowDate(item)}</TD>
                            {/* A change's label is a sentence and its
                                description a before → after line: prose,
                                which wraps, rather than pushing the amount
                                off the card. A money row's stay one-line. */}
                            <TD wrap={prose} className={prose ? 'max-w-sm' : undefined}>
                              <LabelWithEmoji item={item} />
                              {isImportedInvoice(item) ? (
                                // The customer, under the numbers: the label
                                // line is the invoice's identity in both systems.
                                <div className="text-xs text-muted-foreground">{item.label}</div>
                              ) : item.detail ? (
                                <div className="text-xs text-muted-foreground">{item.detail}</div>
                              ) : null}
                            </TD>
                            <TD wrap={prose} className="max-w-xs">
                              {item.type === 'change' ? (
                                <ChangeLines item={item} />
                              ) : (
                                <div className="truncate">{item.description ?? '—'}</div>
                              )}
                              {isImportedInvoice(item) ? <SourceFileLink item={item} orgId={batch.orgId} /> : null}
                              {item.proposalNote ? <Note text={item.proposalNote} clamp data-testid="inbox-row-note" /> : null}
                              {reason ? <Failure reason={reason} /> : null}
                            </TD>
                            <TD>
                              <Badge variant={badgeVariant(item)}>{kindLabel(item)}</Badge>
                            </TD>
                            <TD className="text-right">
                              <RowAmount item={item} />
                            </TD>
                            <TD className="text-right">
                              <RowAction
                                item={item}
                                onEdit={() => setEditing(item)}
                                onDiscard={() => discardOne(item, batch.orgId)}
                                onConfirm={() => act.mutate({ path: 'confirm', items: [{ type: item.type, id: item.id, orgId: batch.orgId }] })}
                                confirming={confirmingOne(item)}
                                disabled={busy}
                              />
                            </TD>
                          </TR>
                        );
                      })}
                    </TBody>
                  </Table>
                )}
              </Card>
            );
          })
        )}

        {caveats.length > 0 ? (
          <div className="space-y-1 text-xs text-muted-foreground" role="note" aria-label="Books not listed">
            {caveats.map((caveat) => (
              <p key={caveat}>{caveat}</p>
            ))}
          </div>
        ) : null}
      </div>

      {editing ? (
        <EditSheetFor
          item={editing}
          batch={batches.find((b) => b.items.some((i) => keyOf(i) === keyOf(editing)))}
          onClose={() => setEditing(null)}
          // One row, the open one - through the same discard the bulk bar
          // uses, so the server's refusal and the notice read the same.
          onDiscard={async () => {
            const book = batches.find((b) => b.items.some((i) => keyOf(i) === keyOf(editing)));
            if (!book) return;
            const outcome = await act.mutateAsync({ path: 'discard', items: [{ type: editing.type, id: editing.id, orgId: book.orgId }] });
            if (outcome.failed.length) throw new Error(outcome.failed[0].reason);
            setEditing(null);
          }}
          onSaved={(outcome) => {
            setEditing(null);
            // The rows the sheet could not file alongside stay drafts, with
            // the server's sentence beside each - the same place a failed
            // confirm puts its reason.
            setFailures(new Map(outcome.failed.map((f) => [keyOf(f), f.reason])));
            setNotice(savedNotice(outcome.applied, outcome.failed.length, outcome.categoryLabel));
            // An edit changes no figure, so only the Inbox is stale - and the
            // draft lists on Customers and Items, which read the same rows.
            void queryClient.invalidateQueries({ queryKey: inboxKey(org?.id) });
            void queryClient.invalidateQueries({ queryKey: ['items'] });
            void queryClient.invalidateQueries({ queryKey: ['customers'] });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The sheet for the row's kind. A draft item, a draft customer and a change
 * each have their own (DraftSheets.tsx); an expense or a deposit has the one
 * below, with the history-backed suggestions. All of them go to the row's
 * OWN book - the batch's `orgId` - not the one on the sidebar.
 */
function EditSheetFor({
  item,
  batch,
  onClose,
  onSaved,
  onDiscard,
}: {
  item: InboxItem;
  batch: InboxBatch | undefined;
  onClose: () => void;
  onSaved: (outcome: SaveOutcome) => void;
  onDiscard?: () => Promise<unknown>;
}) {
  const plain = () => onSaved({ applied: 0, failed: [], categoryLabel: '' });
  switch (item.type) {
    case 'item':
      return <ItemEditSheet item={item} orgId={batch?.orgId} onClose={onClose} onSaved={plain} onDiscard={onDiscard} />;
    case 'customer':
      return <CustomerEditSheet item={item} orgId={batch?.orgId} onClose={onClose} onSaved={plain} onDiscard={onDiscard} />;
    case 'change':
      return <ChangeEditSheet item={item} orgId={batch?.orgId} onClose={onClose} onSaved={plain} onDiscard={onDiscard} />;
    default:
      return <EditSheet item={item} batch={batch} onClose={onClose} onSaved={onSaved} onDiscard={onDiscard} />;
  }
}

/** The date a row is about: a change's is its target's; a draft item or customer has only the day it was proposed. */
function rowDate(item: InboxItem): string {
  const date = item.type === 'change' ? (item.target?.date ?? item.date) : item.date;
  return date ? formatDate(date) : '—';
}

/**
 * A home-grown invoice draft is grey (it is decided elsewhere); an imported
 * one is amber like every draft decided here; a change is blue (it touches
 * a record that exists); the rest are amber drafts.
 */
function badgeVariant(item: InboxItem): 'neutral' | 'warning' | 'info' {
  if (item.type === 'invoice') return isImportedInvoice(item) ? 'warning' : 'neutral';
  // A deletion is the one change Confirm cannot be undone from: amber, not blue.
  if (item.type === 'change') return changeKindLabel(item) === 'Delete' ? 'warning' : 'info';
  return 'warning';
}

/** The kind's name on the badge and the card - "Imported invoice" and "Settlement" for the two 0018 shapes. */
function kindLabel(item: InboxItem): string {
  if (isImportedInvoice(item)) return 'Imported invoice';
  if (item.type === 'change') return changeKindLabel(item);
  return KIND_LABEL[item.type];
}

/**
 * The amount column: a money row's own; a change's TARGET's, so the person
 * sees which ₹50,000 is about to be somebody's; a settlement's the payment
 * it would record; an item's rate; nothing for a customer, who has no
 * figure until they are invoiced.
 */
function RowAmount({ item }: { item: InboxItem }) {
  if (hasOwnAmount(item.type)) return <Money value={item.amount} currency={item.currency} />;
  if (item.type === 'change') {
    const settled = settlementFigures(item.target, item.changes, item.target?.currency ?? item.currency, MONEY);
    if (settled) return <Money value={settled.amount} currency={item.target?.currency ?? item.currency} />;
    return item.target?.amount ? (
      <Money value={item.target.amount} currency={item.target.currency ?? item.currency} />
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }
  if (item.type === 'item') {
    // The unit is in the row's detail ("service · per hour"); the column is the rate alone.
    const rate = item.item?.rate ?? (Number(item.amount) > 0 ? item.amount : null);
    return rate ? <Money value={rate} currency={item.currency} /> : <span className="text-muted-foreground">—</span>;
  }
  return <span className="text-muted-foreground">—</span>;
}

/**
 * What a change would do, as before → after, and the record it would do it
 * to. The first line is the one Confirm is pressed on (see
 * `beforeAfterLine`); the second says which deposit, in the words the
 * statement used, so a person can find it on Banking if they doubt it.
 */
function ChangeLines({ item }: { item: InboxItem }) {
  if (isSettlement(item)) return <SettlementLines item={item} />;
  const line = beforeAfterLine(item.target, item.changes);
  const target = item.target;
  const facts = [target?.date ? formatDate(target.date) : null, target?.description?.trim() || null].filter(
    (f): f is string => !!f,
  );
  return (
    <div className="min-w-0">
      {line ? (
        <div className="text-sm" data-testid="inbox-change-line">
          {line}
        </div>
      ) : null}
      {facts.length ? (
        <div className="truncate text-xs text-muted-foreground" data-testid="inbox-change-target">
          {facts.join(' · ')}
        </div>
      ) : null}
      {/* The bill is already on the expense as its source (0018): said
          here, because "attach the receipt" is the other half of what a
          matched bill does, and the person should not go looking for it. */}
      {target?.type === 'expense' && target.current?.hasSourceFile ? (
        <div className="text-xs text-success" data-testid="inbox-change-receipt">
          receipt attached ✓
        </div>
      ) : null}
    </div>
  );
}

/**
 * A settlement, as before → after in figures: the deposit as Banking shows
 * it and how it is filed today, the invoice it would pay, the invoice's
 * balance before and after - and the one sentence that makes this row
 * different from every other change: confirming records a payment.
 */
function SettlementLines({ item }: { item: InboxItem }) {
  const target = item.target;
  const changes = item.changes;
  const currency = target?.currency ?? item.currency;
  const figures = settlementFigures(target, changes, currency, MONEY);
  const depositAmount = changes?.depositAmount ? <Money value={changes.depositAmount} currency={currency} /> : null;
  const depositWhen = changes?.depositDate ? formatDateLong(changes.depositDate) : null;
  return (
    <div className="min-w-0 space-y-0.5">
      <div className="text-sm" data-testid="inbox-settlement-line">
        Deposit{depositWhen ? ` ${depositWhen}` : ''} {depositAmount}
        {figures?.withheld.map((w) => (
          <span key={w.label}>
            {' '}+ <Money value={w.amount} currency={currency} /> {w.label}
          </span>
        ))}
        {figures?.depositFiling ? ` · ${figures.depositFiling}` : ''} → payment against {figures?.invoice ?? target?.number ?? '—'}
      </div>
      {changes?.depositDescription?.trim() ? (
        <div className="truncate text-xs text-muted-foreground" data-testid="inbox-change-target">
          {changes.depositDescription.trim()}
        </div>
      ) : null}
      {figures ? (
        <div className="text-xs text-muted-foreground" data-testid="inbox-settlement-balance">
          Balance <Money value={figures.balanceBefore} currency={currency} /> → <Money value={figures.balanceAfter} currency={currency} />
        </div>
      ) : null}
      <div className="text-xs text-muted-foreground" data-testid="inbox-settlement-note">
        Confirming records a payment{figures ? <> of <Money value={figures.amount} currency={currency} /></> : null}
        {figures?.withheld.length ? ' - the cash plus what was withheld -' : ''} and links this deposit to it.
      </div>
    </div>
  );
}

/**
 * The original document an imported invoice was made from, as a link that
 * opens it. The org is in the PATH because a link is a navigation and cannot
 * carry `x-org-id` - see Attachments.tsx - and it is the ROW's book, not the
 * sidebar's: the Inbox spans the books.
 */
function SourceFileLink({ item, orgId }: { item: InboxItem; orgId: string }) {
  const file = importedFieldsOf(item).sourceFile;
  if (!file) return null;
  return (
    <a
      href={`/v1/files/${orgId}/${file.id}`}
      target="_blank"
      rel="noopener"
      className="mt-0.5 inline-flex min-h-9 max-w-full items-center gap-1 text-xs text-primary hover:underline touch:min-h-11"
      data-testid="inbox-source-file"
    >
      <span aria-hidden>📎</span>
      <span className="truncate">Source: {file.filename}</span>
    </a>
  );
}

/**
 * One button per book, "All books" first, each in its book's colour.
 *
 * The pressed one is FILLED in its token and the rest are outlined in theirs,
 * so the colour says which book and the fill says which is chosen - and
 * `aria-pressed` says the second to a screen reader, because a fill is a
 * colour cue. The count is on the button: it is what the person came to
 * find out. Wraps on a phone rather than scrolling off the edge.
 */
function BookButtons({
  books,
  total,
  value,
  onChange,
}: {
  books: InboxBook[];
  total: number;
  value: string | null;
  onChange: (orgId: string | null) => void;
}) {
  return (
    <div role="group" aria-label="Books" className="flex flex-wrap gap-2" data-testid="inbox-books">
      <button
        type="button"
        aria-pressed={value === null}
        onClick={() => onChange(null)}
        className={cn(
          'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors touch:min-h-11',
          value === null
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input text-foreground hover:bg-accent',
        )}
      >
        All books
        <span className="text-xs opacity-80">{total}</span>
      </button>
      {books.map((book) => {
        const pressed = value === book.orgId;
        return (
          <button
            key={book.orgId}
            type="button"
            aria-pressed={pressed}
            onClick={() => onChange(pressed ? null : book.orgId)}
            data-testid="inbox-book-button"
            data-org-id={book.orgId}
            className={cn(
              'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors touch:min-h-11',
              // primary-foreground on the FULL-STRENGTH token, as the colour
              // picker's check glyph is: the palette is validated as text on
              // the card in both themes, so the card's own text colour on it
              // clears the same ratio the other way round.
              pressed ? 'text-primary-foreground' : 'hover:bg-accent',
            )}
            style={
              pressed
                ? { backgroundColor: colourSolid(book.colour), borderColor: colourSolid(book.colour) }
                : { borderColor: colourSolid(book.colour), color: colourSolid(book.colour) }
            }
          >
            {pressed ? null : <ColourDot colour={book.colour} className="h-2 w-2" />}
            {book.name}
            <span className="text-xs opacity-80">{book.total}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * "Move to ▾": the other books, each with its dot. Lists every book the
 * selected rows are not ALL already in; a target some of them are in gets
 * only the rest.
 */
function MoveMenu({
  books,
  selected,
  disabled,
  loading,
  onMove,
}: {
  books: InboxBook[];
  selected: BookRef[];
  disabled: boolean;
  loading: boolean;
  onMove: (to: InboxBook) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const targets = moveTargets(books, selected);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        disabled={disabled || targets.length === 0}
        loading={loading}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ArrowRightLeft className="size-4" aria-hidden />
        Move to
        <ChevronDown className="size-4" aria-hidden />
      </Button>
      {open ? (
        <div
          role="menu"
          aria-label="Move to"
          className="absolute right-0 z-20 mt-1 min-w-44 animate-slide-up overflow-hidden rounded-md border border-border bg-popover shadow-lg"
        >
          {targets.map((book) => (
            <button
              key={book.orgId}
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false);
                onMove(book);
              }}
              className="flex min-h-9 w-full items-center gap-2 px-3 text-left text-sm hover:bg-accent active:bg-accent touch:min-h-11"
            >
              <ColourDot colour={book.colour} className="h-2 w-2" />
              <span className="truncate">{book.name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The category's emoji on a money row; the kind's own mark - 📦, 🧑, 🔗 - on
 * a catalogue or change row that has none; 📄 on an imported invoice, whose
 * label is its identity in both systems: "INV-0007 · originally INV-2024-017
 * in Zoho".
 */
function LabelWithEmoji({ item, truncate }: { item: InboxItem; truncate?: boolean }) {
  const imported = isImportedInvoice(item);
  const mark = imported ? '📄' : (item.emoji ?? KIND_ICON[item.type] ?? null);
  const fields = imported ? importedFieldsOf(item) : null;
  const originally = fields ? originallyLine(fields.externalNumber, fields.sourceSystem, fields.number) : null;
  const label = fields ? [fields.number ?? item.detail ?? item.label, originally].filter(Boolean).join(' · ') : item.label;
  return (
    // Capped at its cell: a change's label is a whole sentence. In a table
    // cell it wraps; on a phone card - one line by design - it truncates,
    // because an inline-flex wider than the card is a card that scrolls
    // sideways.
    <span className="inline-flex max-w-full min-w-0 items-start gap-1.5">
      {mark ? (
        <span aria-hidden className="shrink-0">
          {mark}
        </span>
      ) : null}
      <span className={cn('min-w-0', truncate && 'truncate')} data-testid={imported ? 'inbox-imported-label' : undefined}>
        {label}
      </span>
    </span>
  );
}

/** The sentence the server refused with, beside the row it refused. */
function Failure({ reason }: { reason?: string }) {
  if (!reason) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-destructive">
      {reason}
    </p>
  );
}

/**
 * Edit for a row that can be edited here; a link for an invoice, which has a
 * whole page and is sent from there; for an IMPORTED invoice, a Confirm of
 * this one row worded for what it does - "Record as issued" - beside the
 * link, because it was sent from the old system and there is nothing left
 * to send. A settlement links to the invoice it would settle.
 */
function RowAction({
  item,
  onEdit,
  onDiscard,
  onConfirm,
  confirming,
  disabled,
}: {
  item: InboxItem;
  onEdit: () => void;
  /** This one row's proposal, thrown away - offered on a correction, which has no sheet to do it from. */
  onDiscard: () => Promise<unknown>;
  onConfirm: () => void;
  confirming: boolean;
  disabled: boolean;
}) {
  const action = rowActionFor(item);
  if (action === 'discard-only') {
    return <DiscardDraft onDiscard={onDiscard} question="Delete this proposed vendor? It was never added." />;
  }
  if (action === 'correction') {
    return <DiscardDraft onDiscard={onDiscard} question="Discard this correction? The record stays as it is." />;
  }
  if (action === 'edit') {
    return (
      <Button variant="ghost" size="sm" onClick={onEdit}>
        Edit
      </Button>
    );
  }
  if (action === 'open-proforma') {
    // A quote is changed and sent on its own page; the row only finds it.
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-1">
        <DiscardDraft onDiscard={onDiscard} question="Delete this draft quote? It was never sent." />
        <Button asChild variant="link" size="sm" className="px-1">
          <Link to={`/proformas/${item.id}`} data-testid="inbox-open-proforma">
            Open quote
          </Link>
        </Button>
      </span>
    );
  }
  const invoiceId = item.type === 'change' ? item.target?.id : item.id;
  const open = invoiceId ? (
    <Button asChild variant="link" size="sm" className="px-1">
      <Link to={`/invoices/${invoiceId}`}>Open invoice</Link>
    </Button>
  ) : null;
  // A line the agent read wrong is fixed in the composer, before it is
  // recorded; the agent itself cannot change an amount once proposed.
  const edit = invoiceId && item.type === 'invoice' ? (
    <Button asChild size="sm" variant="ghost">
      <Link to={`/invoices/${invoiceId}/edit`} data-testid="inbox-edit-invoice">Edit</Link>
    </Button>
  ) : null;
  if (action === 'open-invoice') return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      {edit}
      {open}
    </span>
  );
  // The customer it names may be a draft in the same card. The server
  // refuses to issue an invoice to a draft customer - "Confirm all" orders
  // the two - so the row says so before the button is pressed alone.
  const customerDraft = importedFieldsOf(item).customerStatus === 'draft';
  return (
    <span className="inline-flex max-w-full flex-col items-end gap-0.5">
      <span className="inline-flex flex-wrap items-center justify-end gap-1">
        <Button size="sm" variant="secondary" loading={confirming} disabled={disabled} onClick={onConfirm} data-testid="inbox-record-issued">
          Record as issued
        </Button>
        {edit}
        {open}
      </span>
      {customerDraft ? (
        <span className="text-right text-xs text-muted-foreground" data-testid="inbox-record-issued-hint">
          Its customer is a draft here too - Confirm all records both.
        </span>
      ) : null}
    </span>
  );
}

interface CategoryOption {
  id: string;
  name: string;
  emoji?: string | null;
  colour?: string | null;
}

interface AccountOption {
  id: string;
  name: string;
  isInvestment?: boolean;
}

/** What the sheet did besides the row it was opened on. */
interface SaveOutcome {
  applied: number;
  failed: Array<Ref & { reason: string }>;
  /** "✈️ Travel" - for the sentence above the list. */
  categoryLabel: string;
}

/**
 * Fix what a model reads off a statement row and can read wrongly - the
 * date, the amount, the words - and FILE it: the category and the account,
 * which used to be a read-only line at the top of this sheet. An expense
 * goes through its own PATCH, which refuses `status`; a deposit through the
 * Inbox's, which refuses anything that is not a draft. Neither can move a
 * counted figure. Both go to the row's OWN book - the batch's `orgId` - not
 * the one on the sidebar, and so do the lists the selects are filled from.
 *
 * ── WHY THE SHEET REMEMBERS ──
 * The owner filing 25 statement rows one by one asked for "a dropdown to
 * choose past options used, to make it search and tag faster". So as the
 * description stands - or is retyped - the sheet asks the server what this
 * book filed the same payee under before (`GET /inbox/suggestions`, recorded
 * rows only, never a draft) and offers it three ways: the past descriptions
 * under the field, the categories and accounts as one-tap chips, and - when
 * the history is unambiguous and the row is still on the seeded default -
 * the category PRESELECTED, said so in a line, with an Undo. A row the
 * person has already put somewhere is never changed under them: the
 * preselect fires only on "Other expenses" / "Other income" / nothing, and
 * never again once the select has been touched.
 *
 * ── WHY IT CAN FILE THE BATCH'S OTHER ROWS ──
 * Three rides in the same auto arrive as three rows. Filing the first under
 * Travel and being asked whether to file the other two the same way - one
 * tick, on by default - is most of what "faster" means. Category only,
 * never the account: the same payee is paid from whichever card was in the
 * hand. One PATCH per row, and a row that fails leaves the others filed
 * with its reason beside it on the list.
 */
function EditSheet({
  item,
  batch,
  onClose,
  onSaved,
  onDiscard,
}: {
  item: InboxItem;
  /** The batch the row is in: its book, and the rows beside it. */
  batch: InboxBatch | undefined;
  onClose: () => void;
  onSaved: (outcome: SaveOutcome) => void;
  /** Throw this one draft away. See `DiscardDraft`. */
  onDiscard?: () => Promise<unknown>;
}) {
  useEscapeToClose(onClose);
  // Invoices never open this sheet - RowAction links to the page instead.
  const type: SuggestType = item.type === 'deposit' ? 'deposit' : 'expense';
  const orgId = batch?.orgId;
  const [form, setForm] = useState({
    date: item.date,
    amount: item.amount,
    description: item.description ?? '',
    categoryId: item.categoryId ?? '',
    accountId: item.accountId ?? '',
  });
  /**
   * The preselect the sheet made, so Undo can put back what was there. Null
   * when the sheet chose nothing, or the person undid it.
   */
  const [suggested, setSuggested] = useState<{ categoryId: string; previous: string; count: number; payee: string } | null>(null);
  /** Once the person has set the category themselves, the sheet never chooses again. */
  const [categoryTouched, setCategoryTouched] = useState(false);
  /**
   * Income or capital introduced, on a deposit. Capital takes no income
   * category - that is what capital is - so choosing it hides the category,
   * stops the sheet preselecting one from history, and saves the row with
   * none. Filing an owner's contribution as income overstates the year's
   * income by it, which is the whole reason this choice exists.
   */
  const [kind, setKind] = useState<DepositKind>(item.depositKind ?? 'income');
  const capital = type === 'deposit' && kind === 'capital';
  const [applyToBatch, setApplyToBatch] = useState(true);

  const categories = useQuery({
    queryKey: type === 'expense' ? ['expense-categories', orgId] : ['income-categories', orgId],
    queryFn: () =>
      api.get<{ items: CategoryOption[] }>(type === 'expense' ? '/expenses/categories' : '/income-categories', { orgId }),
    enabled: !!orgId,
  });
  const accounts = useQuery({
    queryKey: ['banking', orgId, 'accounts'],
    queryFn: () => api.get<{ items: AccountOption[] }>('/banking/accounts', { orgId }),
    enabled: !!orgId,
  });
  // Debounced, so retyping the description is not a request per keystroke;
  // the FIRST value is the row's own description, so the sheet asks at once.
  const settled = useDebounced(form.description, 300);
  const suggestions = useQuery({
    queryKey: ['inbox-suggest', orgId, type, settled],
    queryFn: () => api.get<Suggestions>(`/inbox/suggestions${qs({ type, q: settled })}`, { orgId }),
    enabled: !!orgId && settled.trim().length > 0,
  });
  const history = suggestions.data ?? EMPTY_SUGGESTIONS;

  // Until the list arrives, the row's own category is the one option - and
  // a category the sheet preselected from history is added if the list does
  // not hold it yet - so the select never shows blank for a value it holds.
  const categoryOptions: CategoryOption[] = useMemo(() => {
    const listed = categories.data?.items ?? (item.categoryId ? [{ id: item.categoryId, name: item.label, emoji: item.emoji }] : []);
    if (!form.categoryId || listed.some((c) => c.id === form.categoryId)) return listed;
    const fromHistory = history.categories.find((c) => c.id === form.categoryId);
    return fromHistory ? [...listed, fromHistory] : listed;
  }, [categories.data, item.categoryId, item.label, item.emoji, form.categoryId, history.categories]);
  // An investment only when the draft already names one - never offered.
  const accountOptions: AccountOption[] = (
    accounts.data?.items ?? (item.accountId && item.detail ? [{ id: item.accountId, name: item.detail }] : [])
  ).filter((a) => !a.isInvestment || a.id === form.accountId);
  const selectedCategory = categoryOptions.find((c) => c.id === form.categoryId);
  const selectedAccount = accountOptions.find((a) => a.id === form.accountId);
  const categoryText = capital
    ? DEPOSIT_KIND_LABELS.capital
    : selectedCategory
      ? categoryLabel(selectedCategory.name, selectedCategory.emoji)
      : type === 'deposit'
        ? 'Not sure'
        : categoryLabel(item.label, item.emoji);

  /**
   * The preselect. Fires when the history points one way (see
   * `unambiguousCategory`), the row came in undecided, and the category is
   * still what the row came with or what this sheet chose - never something
   * the person picked. Re-running after it fires finds the category already
   * there and does nothing, so there is no loop.
   */
  const originallyUndecided = isUndecided(type, item.categoryId ? item.label : null);
  useEffect(() => {
    if (capital || categoryTouched || !originallyUndecided || !suggestions.data) return;
    const top = unambiguousCategory(suggestions.data.categories);
    const payee = suggestions.data.payee;
    if (!top || !payee) return;
    const untouched = form.categoryId === (item.categoryId ?? '') || (suggested !== null && form.categoryId === suggested.categoryId);
    if (!untouched || top.id === form.categoryId) return;
    setForm((f) => ({ ...f, categoryId: top.id }));
    setSuggested({ categoryId: top.id, previous: item.categoryId ?? '', count: top.count, payee });
  }, [capital, suggestions.data, categoryTouched, originallyUndecided, form.categoryId, item.categoryId, suggested]);

  const chooseCategory = (categoryId: string) => {
    setCategoryTouched(true);
    setSuggested(null);
    setForm((f) => ({ ...f, categoryId }));
  };
  const undoSuggestion = () => {
    if (!suggested) return;
    setForm((f) => ({ ...f, categoryId: suggested.previous }));
    setSuggested(null);
    setCategoryTouched(true);
  };

  // The batch's other rows for this payee that are not yet where this one
  // is going. Category only - see the note above.
  const siblings = batch
    ? batchRowsForPayee(batch.items, item, history.payeeKey).filter((row) => row.categoryId !== form.categoryId)
    : [];
  const canApply = !capital && siblings.length > 0 && !!form.categoryId && !!history.payee;

  const save = useMutation({
    mutationFn: async (): Promise<SaveOutcome> => {
      if (type === 'expense') {
        await api.patch(
          `/expenses/${item.id}`,
          {
            date: form.date,
            amount: form.amount,
            notes: form.description,
            categoryId: form.categoryId,
            ...(form.accountId ? { paidThroughAccountId: form.accountId } : {}),
          },
          { orgId },
        );
      } else {
        await api.patch(
          `/inbox/deposits/${item.id}`,
          {
            date: form.date,
            amount: form.amount,
            description: form.description || null,
            kind,
            incomeCategoryId: capital ? null : form.categoryId || null,
            ...(form.accountId ? { accountId: form.accountId } : {}),
          },
          { orgId },
        );
      }
      const outcome: SaveOutcome = { applied: 0, failed: [], categoryLabel: categoryText };
      if (!applyToBatch || !canApply) return outcome;
      // One request per row, and on: a row that fails is reported and the
      // rest are filed, exactly as a confirm is.
      for (const row of siblings) {
        try {
          if (type === 'expense') await api.patch(`/expenses/${row.id}`, { categoryId: form.categoryId }, { orgId });
          else await api.patch(`/inbox/deposits/${row.id}`, { incomeCategoryId: form.categoryId }, { orgId });
          outcome.applied += 1;
        } catch (e) {
          outcome.failed.push({ type: row.type, id: row.id, reason: e instanceof Error ? e.message : 'Something went wrong' });
        }
      }
      return outcome;
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const categoryChips = history.categories.map((c) => ({ id: c.id, name: c.name, emoji: c.emoji, colour: c.colour, count: c.count }));
  const accountChips = history.accounts.map((a) => ({ id: a.id, name: a.name, count: a.count }));

  return (
    <Sheet label={`Edit ${KIND_LABEL[item.type].toLowerCase()}`} onClose={onClose}>
      <SheetHeader title={<>Edit {KIND_LABEL[item.type].toLowerCase()}</>} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          {/* What the row will be filed as, LIVE: it follows the selects. */}
          <p className="text-sm text-muted-foreground" data-testid="inbox-edit-summary">
            {categoryText}
            {selectedAccount?.name ?? item.detail ? ` · ${selectedAccount?.name ?? item.detail}` : ''}
          </p>
          {item.proposalNote ? <Note text={item.proposalNote} /> : null}
          <Input label="Date" type="date" required value={form.date} onChange={set('date')} error={error?.fieldError('date')} />
          <Input
            label="Amount"
            required
            inputMode="decimal"
            value={form.amount}
            onChange={set('amount')}
            error={error?.fieldError('amount')}
          />
          <div className="flex flex-col gap-1.5">
            <Textarea label="Description" rows={2} className="min-h-0" value={form.description} onChange={set('description')} />
            <PastDescriptions
              options={history.descriptions}
              current={form.description}
              onPick={(description) => setForm((f) => ({ ...f, description }))}
              testId="inbox-past-descriptions"
            />
          </div>

          {type === 'deposit' ? (
            <div className="flex flex-col gap-1.5">
              <SelectField id="inbox-edit-kind" label="What is it?" required value={kind} onChange={(v) => setKind(v as DepositKind)}>
                {DEPOSIT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {DEPOSIT_KIND_LABELS[k]}
                  </option>
                ))}
              </SelectField>
              {capital ? (
                <p className="text-xs text-muted-foreground" data-testid="inbox-edit-capital-hint">
                  Money a partner or owner put in. Not income: it lands on the balance and in no income figure, so it
                  takes no category.
                </p>
              ) : null}
            </div>
          ) : null}

          {capital ? null : (
            <div className="flex flex-col gap-1.5">
              <SelectField
                id="inbox-edit-category"
                label={type === 'expense' ? 'Category' : 'Income category'}
                required={type === 'expense'}
                value={form.categoryId}
                onChange={chooseCategory}
                error={error?.fieldError(type === 'expense' ? 'categoryId' : 'incomeCategoryId')}
              >
                {type === 'deposit' ? <option value="">Not sure</option> : null}
                {categoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {categoryLabel(c.name, c.emoji)}
                  </option>
                ))}
              </SelectField>
              {suggested && form.categoryId === suggested.categoryId ? (
                <SuggestedLine
                  text={suggestedLine(type, suggested.count, suggested.payee)}
                  onUndo={undoSuggestion}
                  testId="inbox-suggested"
                />
              ) : null}
              {history.payee ? (
                <SuggestChips
                  label={`Used before for ${history.payee}`}
                  chips={categoryChips}
                  value={form.categoryId || null}
                  onPick={(chip) => chooseCategory(chip.id)}
                  testId="inbox-suggest-categories"
                />
              ) : null}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <SelectField
              id="inbox-edit-account"
              label={type === 'expense' ? 'Paid from' : 'Into account'}
              required
              value={form.accountId}
              onChange={(accountId) => setForm((f) => ({ ...f, accountId }))}
              error={error?.fieldError(type === 'expense' ? 'paidThroughAccountId' : 'accountId')}
            >
              {accountOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </SelectField>
            {history.payee ? (
              <SuggestChips
                label={type === 'expense' ? `${history.payee} was paid from` : `${history.payee} paid into`}
                chips={accountChips}
                value={form.accountId || null}
                onPick={(chip) => setForm((f) => ({ ...f, accountId: chip.id }))}
                testId="inbox-suggest-accounts"
              />
            ) : null}
          </div>

          {canApply ? (
            <label className="flex min-h-11 items-start gap-2 text-sm" data-testid="inbox-apply-batch">
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0 rounded border-input"
                checked={applyToBatch}
                onChange={(e) => setApplyToBatch(e.target.checked)}
              />
              <span>{applyToBatchLabel(siblings.length, history.payee ?? '', categoryText)}</span>
            </label>
          ) : null}

          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {onDiscard ? <DiscardDraft onDiscard={onDiscard} /> : null}
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
